import { Request, Response, NextFunction, RequestHandler } from 'express';

/**
 * Options for response formatter middleware
 */
export interface ResponseFormatterOptions {
  /**
   * Whether to wrap successful responses
   * @default true
   */
  wrapSuccess?: boolean;

  /**
   * Whether to include request ID in response
   * @default true
   */
  includeRequestId?: boolean;

  /**
   * Default success message
   * @default 'Operation successful'
   */
  defaultSuccessMessage?: string;

  /**
   * Default error message
   * @default 'Operation failed'
   */
  defaultErrorMessage?: string;
}

/**
 * Extension to Express Response interface for custom response methods
 */
export interface FormattedResponse extends Response {
  success(data?: any, message?: string, meta?: Record<string, any>): Response;
  created(data?: any, message?: string): Response;
  noContent(): Response;
  error(message?: string, statusCode?: number, code?: string, details?: any): Response;
  paginated(items: any[], page: number, limit: number, totalItems: number, message?: string): Response;
}

/**
 * Create middleware to format responses
 *
 * @param options Response formatter options
 * @returns Express middleware
 */
export function responseFormatterMiddleware(options: ResponseFormatterOptions = {}): RequestHandler {
  const wrapSuccess = options.wrapSuccess !== false;
  const includeRequestId = options.includeRequestId !== false;
  const defaultSuccessMessage = options.defaultSuccessMessage || 'Operation successful';
  const defaultErrorMessage = options.defaultErrorMessage || 'Operation failed';

  return (req: Request, res: Response, next: NextFunction) => {
    // Add success response method
    (res as FormattedResponse).success = function(data?: any, message?: string, meta?: Record<string, any>) {
      const response: Record<string, any> = wrapSuccess
        ? {
          success: true,
          message: message || defaultSuccessMessage,
          data: data || null
        }
        : data || {};

      // Add metadata if provided
      if (meta) {
        response.meta = meta;
      }

      // Add request ID if available and enabled
      if (includeRequestId && (req as any).correlationId) {
        if (wrapSuccess) {
          response.requestId = (req as any).correlationId;
        } else {
          response._requestId = (req as any).correlationId;
        }
      }

      return res.json(response);
    };

    // Add created response method (201 Created)
    (res as FormattedResponse).created = function(data?: any, message?: string) {
      res.status(201);
      return (res as FormattedResponse).success(data, message || 'Resource created successfully');
    };

    // Add no content response method (204 No Content)
    (res as FormattedResponse).noContent = function() {
      return res.status(204).end();
    };

    // Add error response method
    (res as FormattedResponse).error = function(message?: string, statusCode = 400, code?: string, details?: any) {
      const response: Record<string, any> = {
        error: {
          message: message || defaultErrorMessage,
          code: code || 'BAD_REQUEST',
          timestamp: new Date().toISOString()
        }
      };

      // Add details if provided
      if (details) {
        response.error.details = details;
      }

      // Add request ID if available and enabled
      if (includeRequestId && (req as any).correlationId) {
        response.error.requestId = (req as any).correlationId;
      }

      return res.status(statusCode).json(response);
    };

    // Add paginated response method
    (res as FormattedResponse).paginated = function(
      items: any[],
      page: number,
      limit: number,
      totalItems: number,
      message?: string
    ) {
      const totalPages = Math.ceil(totalItems / limit);

      return (res as FormattedResponse).success(
        items,
        message || 'Items retrieved successfully',
        {
          pagination: {
            page,
            limit,
            totalItems,
            totalPages,
            hasNextPage: page < totalPages,
            hasPreviousPage: page > 1
          }
        }
      );
    };

    next();
  };
}

// Extend Express Response interface
declare global {
  namespace Express {
    interface Response {
      success(data?: any, message?: string, meta?: Record<string, any>): Response;
      created(data?: any, message?: string): Response;
      noContent(): Response;
      error(message?: string, statusCode?: number, code?: string, details?: any): Response;
      paginated(items: any[], page: number, limit: number, totalItems: number, message?: string): Response;
    }
  }
}
